/**
 * CR-5534: Reusable sub-components for the Approval Register matrix.
 *
 * Extracted from ApprovalRegister.tsx to keep that file under the
 * 500-line budget. Covers the matrix cell button (RAG dot + tooltip)
 * and the certificate download link (which needs an extra detail
 * fetch because the matrix endpoint only carries `has_certificate`).
 */
import { useEffect, useState } from 'react';
import { Circle, Download } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { qualityAPI } from '../lib/api';
import type { AccreditationStatus, ApprovalRegisterCell } from '../lib/api';

/**
 * Matrix dot treatment per status.
 *
 * CR-7171: `no_expiry` used to carry the same `#22C55E` as `in_date`, which
 * made a certificate nobody had dated pixel-identical to one checked and known
 * to be current — on an aerospace approval register, the exact confusion the
 * register exists to prevent. It is now drawn as a hollow ring: unfilled, so it
 * reads as "no status recorded" rather than a fourth colour competing with the
 * three-tier expiry scale.
 */
/**
 * CR-7171: the two things a blank expiry can mean, worded once. Imported by
 * every surface that renders the state so the matrix, the legend and the
 * supplier profile cannot drift into three phrasings of the same fact.
 */
export const NO_EXPIRY_RECORDED_LABEL = 'No expiry recorded';
export const NO_EXPIRY_DELIBERATE_LABEL = 'Does not expire';

export const STATUS_STYLES: Record<
  AccreditationStatus,
  { fill: string; stroke: string; label: string }
> = {
  in_date: { fill: '#22C55E', stroke: '#22C55E', label: 'In date' },
  expiring: { fill: '#F59E0B', stroke: '#F59E0B', label: 'Expiring soon' },
  expired: { fill: '#EF4444', stroke: '#EF4444', label: 'Expired' },
  no_expiry: { fill: 'none', stroke: '#64748B', label: NO_EXPIRY_RECORDED_LABEL },
};

interface MatrixCellProps {
  cell: ApprovalRegisterCell | undefined;
  expiryFormat: (d: string) => string;
  canAdd: boolean;
  onClick: () => void;
}

export function MatrixCell({ cell, expiryFormat, canAdd, onClick }: MatrixCellProps) {
  if (!cell) {
    return (
      <button
        onClick={onClick}
        disabled={!canAdd}
        className={`inline-block w-6 h-6 rounded-full border border-dashed border-gray-400 ${
          canAdd ? 'hover:bg-gray-100' : 'cursor-default opacity-60'
        }`}
        title={canAdd ? 'Add accreditation' : 'Not held'}
      />
    );
  }
  const style = STATUS_STYLES[cell.status];
  // CR-7171: a ticked "no expiry applies" is a statement about the
  // certificate; a bare blank is a gap in the register. Same dot, different
  // sentence — the reader should be able to tell which one they are looking at.
  const statusLabel =
    cell.status === 'no_expiry' && cell.no_expiry_applies
      ? NO_EXPIRY_DELIBERATE_LABEL
      : style.label;
  const tooltipLines = [
    statusLabel,
    cell.expiry_date ? `Expires ${expiryFormat(cell.expiry_date)}` : null,
    cell.certificate_number ? `Cert #${cell.certificate_number}` : null,
  ].filter(Boolean) as string[];

  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className="p-1 rounded-full hover:bg-muted/50 transition-colors"
          >
            <Circle
              className="h-6 w-6"
              fill={style.fill}
              stroke={style.stroke}
              strokeWidth={2.5}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-0.5 text-xs">
            {tooltipLines.map((l) => (
              <div key={l}>{l}</div>
            ))}
            <div className="italic opacity-70">Click for details</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function LegendItem({
  fill,
  stroke,
  label,
}: {
  fill: string;
  stroke: string;
  label: string;
}) {
  return (
    <span className="flex items-center gap-2 text-sm">
      <Circle className="h-4 w-4" fill={fill} stroke={stroke} />
      {label}
    </span>
  );
}

export function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

export function DetailFileLink({ bindingId }: { bindingId: number }) {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>('certificate');

  useEffect(() => {
    setLoading(true);
    qualityAPI
      .getSupplierAccreditation(bindingId)
      .then((res) => {
        setUrl(res.data.certificate_file_url);
        if (res.data.certificate_file_name) setFilename(res.data.certificate_file_name);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [bindingId]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading certificate…</div>;
  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={filename}
      className="inline-flex items-center text-sm text-primary hover:underline"
    >
      <Download className="h-4 w-4 mr-1" />
      Download certificate ({filename})
    </a>
  );
}
